import { type FC, useEffect, useState } from "react";

import type { TraitCondition } from "@hightouch/lib/query/visual/types";
import {
  Button,
  ChakraModal,
  ChakraModalBody,
  ChakraModalContent,
  ChakraModalFooter,
  ChakraModalHeader,
  ChakraModalOverlay,
  CloseIcon,
  Column,
  Heading,
  IconButton,
  Row,
  useToast,
} from "@hightouch/htui";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import { isPresent } from "ts-extras";

import { useFormErrorContext } from "src/contexts/form-error-context";
import type { FullParentModel } from "src/components/audiences/types";
import type {
  AdditionalColumn,
  RelatedColumn,
  TraitColumn,
} from "src/types/visual";

import {
  AudienceTraitForm,
  type AudienceTraitFormProps,
  audienceTraitValidationSchema,
} from "./audience-traits/audience-trait-form";

type Props = {
  trait?: {
    id: string;
    relationship: {
      id: string;
    } | null;
  };
  filterByRelationshipId?: string;
  parentModel?: FullParentModel | null;
  conditions: TraitCondition[];
  onClose: () => void;
  onSubmit: (additionalColumn: AdditionalColumn) => Promise<void>;
};
/*
 * This component should be used if an audience trait is being created from the audience builder.
 */
export const CreateAudienceTraitModal: FC<Readonly<Props>> = ({
  trait,
  filterByRelationshipId,
  parentModel,
  conditions,
  onClose,
  onSubmit,
}) => {
  const { hasValidationErrors } = useFormErrorContext();

  const { toast } = useToast();

  const [submitting, setSubmitting] = useState(false);

  const form = useForm<AudienceTraitFormProps>({
    resolver: yupResolver(
      audienceTraitValidationSchema(parentModel?.connection?.type),
    ),
  });
  const { reset } = form;

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    reset({
      calculation_method: "trait_template",
      template_id: trait?.id,
      relationship_id: trait?.relationship?.id ?? filterByRelationshipId,
      conditions: conditions,
    });
  }, [reset, trait, filterByRelationshipId]);

  const wrappedOnSubmit = async (data: AudienceTraitFormProps) => {
    if (!parentModel) return;

    if (hasValidationErrors()) {
      toast({
        id: "audience-trait-form",
        title: "There was a problem saving your trait",
        message: "Please check your configuration and try again",
        variant: "error",
      });
      return;
    }

    setSubmitting(true);

    const traitColumn: TraitColumn = {
      type: "trait",
      traitDefinitionId: data.template_id,
      conditions: data.conditions,
    };
    const relationshipId =
      trait?.relationship?.id ?? filterByRelationshipId ?? data.relationship_id;
    const relatedColumn: RelatedColumn = {
      type: "related",
      path: [relationshipId?.toString()].filter(isPresent),
      column: traitColumn,
    };

    const additionalColumn: AdditionalColumn = {
      alias: data.name,
      column: relatedColumn,
    };

    await onSubmit(additionalColumn);

    setSubmitting(false);

    onClose();
  };

  const onError = () => {
    toast({
      id: "create-audience-trait-modal-validation",
      variant: "error",
      title: "Audience trait failed to be saved",
      message: "There were errors in your submission",
    });
  };

  return (
    <ChakraModal
      closeOnEsc
      closeOnOverlayClick
      isCentered
      scrollBehavior="inside"
      isOpen
      onClose={onClose}
    >
      <ChakraModalOverlay />
      <ChakraModalContent
        maxHeight="90vh"
        maxWidth="700px"
        padding={0}
        my="auto"
      >
        <ChakraModalHeader
          alignItems="center"
          padding={6}
          borderBottomWidth="hairline"
          borderBottomStyle="solid"
          borderColor="base.border"
        >
          <Row alignItems="center" justifyContent="space-between" width="100%">
            <Heading>New audience trait</Heading>
            <IconButton
              aria-label="Close modal"
              color="text.secondary"
              icon={CloseIcon}
              onClick={onClose}
            />
          </Row>
        </ChakraModalHeader>

        <ChakraModalBody m={0}>
          <Column flex={1} p={6} gap={6} overflowY="auto">
            {parentModel && (
              <FormProvider {...form}>
                <AudienceTraitForm
                  parentModel={parentModel}
                  editConditions
                  hideCalculationMethod
                  filterByRelationshipId={filterByRelationshipId}
                />
              </FormProvider>
            )}
          </Column>
        </ChakraModalBody>

        <ChakraModalFooter
          borderTop="solid 1px"
          borderTopColor="base.border"
          bg="white"
          m={0}
          px={6}
          py={4}
          gap={3}
          borderRadius="inherit"
        >
          <Button onClick={onClose}>Cancel</Button>
          <Button
            isDisabled={submitting}
            isLoading={submitting}
            variant="primary"
            onClick={form.handleSubmit(wrappedOnSubmit, onError)}
          >
            Save trait
          </Button>
        </ChakraModalFooter>
      </ChakraModalContent>
    </ChakraModal>
  );
};
